/**
 * Precios por color del producto de proveedor (petición del CEO, 26/10/2026).
 *
 * Los proveedores suelen dar un precio por color (un teclado negro y el mismo
 * en blanco) y no tiene sentido abrir una referencia por color. Cada línea del
 * histórico de precios (`producto_proveedor_precios.color`) puede llevar un
 * color; sin color es el precio general, válido para cualquiera. Al cotizar,
 * la calculadora pregunta el color, toma el precio de ese color y lo añade al
 * nombre del producto.
 */

/** Color normalizado: sin espacios sobrantes y en mayúsculas. Vacío ⇒ null. */
export function normalizarColor(c: string | null | undefined): string | null {
  const t = (c ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
  return t ? t : null;
}

/** Colores distintos que aparecen en el histórico, en orden alfabético. */
export function coloresDePrecios(precios: ReadonlyArray<{ color?: string | null }>): string[] {
  const set = new Set<string>();
  for (const p of precios) {
    const c = normalizarColor(p.color);
    if (c) set.add(c);
  }
  return [...set].sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * Precio que corresponde a un color: el más reciente de ese color; si no hay,
 * el general más reciente (sin color); y si tampoco, el más reciente de todos.
 * Sin color pedido, el general más reciente y, si no hay, el más reciente.
 * Ordena por fecha descendente; a igual fecha respeta el orden recibido (el
 * servidor ya los da por fecha y creación descendentes).
 */
export function precioParaColor<T extends { fecha: string; color?: string | null }>(
  precios: ReadonlyArray<T>,
  color: string | null | undefined,
): T | null {
  const orden = precios
    .map((p, i) => ({ p, i }))
    .sort((a, b) => String(b.p.fecha).localeCompare(String(a.p.fecha)) || a.i - b.i)
    .map((x) => x.p);
  const c = normalizarColor(color);
  if (c) {
    const delColor = orden.find((p) => normalizarColor(p.color) === c);
    if (delColor) return delColor;
  }
  return orden.find((p) => !normalizarColor(p.color)) ?? orden[0] ?? null;
}

/**
 * Nombre del producto con el color al final. Si el nombre ya lo lleva como
 * palabra suelta (p. ej. «TECLADO TKL NEGRO»), no lo repite.
 */
export function nombreConColor(nombre: string, color: string | null | undefined): string {
  const c = normalizarColor(color);
  const n = nombre.trim();
  if (!c) return n;
  const escapado = c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (new RegExp(`(^|[^\\p{L}\\p{N}])${escapado}($|[^\\p{L}\\p{N}])`, 'iu').test(n)) return n;
  return n ? `${n} ${c}` : c;
}
